import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import {
  createShopCashier,
  fetchShopCashiers,
} from '../services/api';
import type { CashierLoginMethod, ShopCashier } from '../types';
import { currentShiftBadgeKind, currentShiftBadgeLabel, scheduleShiftPath } from '../utils/cashierSchedule';
import { accountStatus, SHIFT } from '@kioskgaming/ui';

const LOGIN_METHOD_OPTIONS: { value: CashierLoginMethod; label: string }[] = [
  { value: 'any', label: 'Any (username, email or phone)' },
  { value: 'username', label: 'Username only' },
  { value: 'email', label: 'Email only' },
  { value: 'phone', label: 'Phone only' },
];

const emptyCreateForm = () => ({
  fullName: '',
  username: '',
  email: '',
  phone: '',
  loginMethod: 'any' as CashierLoginMethod,
  password: '',
});

export default function CashiersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ShopCashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [justCreated, setJustCreated] = useState<ShopCashier | null>(null);

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as any;
    return (
      anyE?.response?.data?.message_localized ||
      anyE?.response?.data?.message ||
      anyE?.message ||
      null
    );
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchShopCashiers();
      if (!res.success) throw new Error(res.message || 'Failed to load Cashiers');
      setRows((res.data || []) as ShopCashier[]);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load Cashiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeCreateModal = () => {
    setCreateOpen(false);
    setForm(emptyCreateForm());
  };

  const onCreate = async () => {
    const { fullName, username, email, phone, loginMethod, password } = form;
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!username.trim()) {
      toast.error('Username is required');
      return;
    }
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!phone.trim()) {
      toast.error('Phone is required');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      const res = await createShopCashier({
        fullName: fullName.trim(),
        username: username.trim(),
        email: email.trim(),
        phone: phone.trim(),
        loginMethod,
        password,
      });
      if (!res.success) throw new Error(res.message || 'Create Cashier failed');
      toast.success('Cashier created');
      closeCreateModal();
      if (res.data?.id) setJustCreated(res.data);
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Create Cashier failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-semibold">Cashiers</h1>
          <button
            type="button"
            className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            onClick={() => setCreateOpen(true)}
          >
            Create Cashier
          </button>
        </div>

        {justCreated ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-sm text-emerald-900">
              <strong>{justCreated.name || justCreated.username}</strong> is ready.
              Assign a shift (and an approved device) so they can deposit and redeem.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                onClick={() => setJustCreated(null)}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                onClick={() => navigate(scheduleShiftPath(justCreated.id))}
              >
                {SHIFT.scheduleShift}
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-3 rounded-xl border border-stone-200 bg-white">
          <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-4 py-3 text-sm text-stone-600">
            <span>{loading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'Cashier' : 'Cashiers'}`}</span>
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50"
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {rows.map((r) => {
              const badgeKind = currentShiftBadgeKind(r.currentShift?.status);
              return (
                <div key={r.id} className="flex items-center gap-3 border-b border-stone-100 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/cashiers/${encodeURIComponent(r.id)}`)}
                    className="min-w-0 flex-1 text-left hover:opacity-80"
                  >
                    <div className="truncate text-sm font-semibold text-stone-900">{r.name}</div>
                    <div className="mt-0.5 truncate text-xs text-stone-500">
                      {r.username}
                      {r.phone ? ` · ${r.phone}` : ''}
                      {r.email ? ` · ${r.email}` : ''}
                    </div>
                    <div className="mt-0.5 text-xs text-stone-500">
                      Status: {accountStatus(r.status).label} • Transactions today: {r.totalTransactionsToday}
                    </div>
                  </button>
                  <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center">
                    <span
                      className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                        badgeKind === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : badgeKind === 'scheduled'
                            ? 'bg-blue-50 text-blue-700'
                            : badgeKind === 'attention'
                              ? 'bg-rose-50 text-rose-700'
                              : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {currentShiftBadgeLabel(r.currentShift?.status)}
                    </span>
                    {r.status === 'active' && badgeKind !== 'active' ? (
                      <button
                        type="button"
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                        onClick={() => navigate(scheduleShiftPath(r.id))}
                      >
                        {SHIFT.scheduleShift}
                      </button>
                    ) : null}
                    {r.currentShift?.id ? (
                      <button
                        type="button"
                        className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                        onClick={() => navigate(`/shifts/${encodeURIComponent(r.currentShift!.id)}`)}
                      >
                        View shift
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {!rows.length && !loading ? (
              <div className="px-4 py-6 text-sm text-stone-600">No Cashiers yet.</div>
            ) : null}
          </div>
        </div>
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Create Cashier</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700">Full name</label>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="Ava Collins"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Username</label>
                <input
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="cashier01"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Email</label>
                <input
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="ava@example.com"
                  type="email"
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="cashier-create-phone" className="text-sm font-medium text-stone-700">Phone</label>
                <input
                  id="cashier-create-phone"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  type="tel"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Login method</label>
                <select
                  value={form.loginMethod}
                  onChange={(e) => setForm((f) => ({ ...f, loginMethod: e.target.value as CashierLoginMethod }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                >
                  {LOGIN_METHOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Password</label>
                <input
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="••••••"
                  type="password"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={closeCreateModal}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={() => void onCreate()}
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}
